import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const addMockItem = (group_id) => {
  return {
    carousel_group_id: group_id,
    media: null,
    headline: '',
    description: '',
    url: '',
  }
}

export const useCarouselAd = ({ cards = [], onChange, tc, tc_run_id }) => {
  const [selectedImageId, setSelectedImageId] = useState(null)
  const [expandedImage, setExpandedImage] = useState(null)
  const carouselData = useMemo(() => cards || [], [cards])
  const carouselDataRef = useRef(carouselData)

  useEffect(() => {
    carouselDataRef.current = carouselData
  }, [carouselData])

  const makeCard = () => {
    const group_id = `${tc}-${tc_run_id}`
    
    return {
      ...addMockItem(group_id),
      id: crypto.randomUUID(),
    }
  }

  const addCard = useCallback((count = 1) => {
    const nextCards = Array.from({ length: count }, () => makeCard())
    onChange([...(carouselDataRef.current || []), ...nextCards])
    setSelectedImageId(nextCards[0].id)
  }, [onChange, tc, tc_run_id])

  const handleUpdateCardField = useCallback((cardId, field, value) => {
    const updatedCards = (carouselDataRef.current || []).map((card) =>
      card.id === cardId ? { ...card, [field]: value } : card,
    )
    onChange(updatedCards)
  }, [onChange])

  const handleRemoveCard = useCallback((cardId) => {
    if (!cardId) return
    const currentCards = carouselDataRef.current || []
    const removedIndex = currentCards.findIndex((card) => card.id === cardId)
    const updatedCards = currentCards.filter((card) => card.id !== cardId)

    if (!updatedCards.length) {
      setSelectedImageId(null)
    } else if (removedIndex > 0) {
      setSelectedImageId(updatedCards[removedIndex - 1].id)
    } else {
      setSelectedImageId(updatedCards[0].id)
    }

    onChange(updatedCards)
  }, [onChange])

  const handleSelectPreviousCard = (cardId) => {
    const currentIndex = carouselData.findIndex((card) => card.id === cardId)
    if (currentIndex <= 0) return
    setSelectedImageId(carouselData[currentIndex - 1].id)
  }

  const handleSelectNextCard = (cardId) => {
    const currentIndex = carouselData.findIndex((card) => card.id === cardId)
    if (currentIndex < 0 || currentIndex >= carouselData.length - 1) return
    setSelectedImageId(carouselData[currentIndex + 1].id)
  }

  const handleOpenExpandedImage = (cardId) => {
    const card = carouselData.find((item) => item.id === cardId)
    if (!card) return
    setExpandedImage({
      ...(card.media || {}),
      id: card.id,
      filename: card.media?.filename || `Card ${carouselData.findIndex((item) => item.id === card.id) + 1}`,
      name: card.media?.name || 'No image',
    })
  }

  const onBack = (currentImage) => {
    const currentIndex = carouselData.findIndex((card) => card.id === currentImage?.id)
    if (currentIndex <= 0) return
    const prevCard = carouselData[currentIndex - 1]
    if (!prevCard) return
    setExpandedImage({
      ...(prevCard.media || {}),
      id: prevCard.id,
      filename: prevCard.media?.filename || `Card ${currentIndex}`,
      name: prevCard.media?.name || 'No image',
    })
  }

  const onNext = (currentImage) => {
    const currentIndex = carouselData.findIndex((card) => card.id === currentImage?.id)
    if (currentIndex < 0 || currentIndex >= carouselData.length - 1) return
    const nextCard = carouselData[currentIndex + 1]
    if (!nextCard) return
    setExpandedImage({
      ...(nextCard.media || {}),
      id: nextCard.id,
      filename: nextCard.media?.filename || `Card ${currentIndex + 2}`,
      name: nextCard.media?.name || 'No image',
    })
  }

  const expandedIndex = carouselData.findIndex((card) => card.id === expandedImage?.id)
  const isFirstItem = expandedIndex <= 0
  const isLastItem = expandedIndex === carouselData.length - 1

  useEffect(() => {
    if (!carouselData.length) {
      setSelectedImageId(null)
      return
    }

    if (!selectedImageId || !carouselData.some((card) => card.id === selectedImageId)) {
      setSelectedImageId(carouselData[0].id)
    }
  }, [carouselData, selectedImageId])

  return {
    carouselData,
    selectedImageId,
    setSelectedImageId,
    expandedImage,
    setExpandedImage,
    addCard,
    handleUpdateCardField,
    handleRemoveCard,
    handleSelectPreviousCard,
    handleSelectNextCard,
    handleOpenExpandedImage,
    onBack,
    onNext,
    isFirstItem,
    isLastItem,
  }
}